import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Calendar, MapPin, Clock, Stethoscope, Filter, List, Map as MapIcon, CalendarDays,
  Search, SortDesc, Bookmark, BookmarkCheck, TrendingUp, DollarSign, RefreshCw,
  ArrowUpRight, X, Sparkles, Building2, BadgeCheck,
} from "lucide-react";

/* ---------------- Catalogs ---------------- */
const SPECIALTIES = ["ICU", "ER", "Med-Surg", "OR", "Cath Lab", "PACU", "L&D", "NICU",
                     "Tele", "Psych", "Oncology", "Respiratory Therapy", "Physical Therapy", "Radiology"];
const STATES = ["TX","CA","NY","FL","IL","AZ","GA","PA","OH","MI","NC","CO","WA","MA","NV","TN"];
const SHIFT_TYPES = ["Day", "Night", "Evening", "Rotating"];
const SORT_OPTIONS = [
  { v: "rate_desc",  l: "Rate — high to low" },
  { v: "rate_asc",   l: "Rate — low to high" },
  { v: "date_asc",   l: "Soonest first" },
  { v: "date_desc",  l: "Latest first" },
];

const BOOKMARKS_KEY = "tc_shift_bookmarks_v1";

/* ---------------- Helpers ---------------- */
function useBookmarks() {
  const [ids, setIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);
  return { ids, toggle };
}

function relativeDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff > 0 && diff < 7) return `In ${diff} days`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/* ---------------- Page ---------------- */
export default function ShiftMarketplace() {
  const { user } = useAuth();
  const bookmarks = useBookmarks();

  const [shifts, setShifts] = useState([]);
  const [view, setView] = useState("list");          // list, calendar, map, saved
  const [filters, setFilters] = useState({ specialty: "", state: "", shiftType: "", q: "", minRate: "" });
  const [sort, setSort] = useState("rate_desc");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const params = {};
      if (filters.specialty) params.specialty = filters.specialty;
      if (filters.state)     params.state     = filters.state;
      const { data } = await api.get("/shifts", { params });
      setShifts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [filters.specialty, filters.state]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/shifts/${id}/action`, { action });
      toast.success(action === "accept" ? "Shift accepted — check your email" :
                     action === "decline" ? "Shift declined" : "Shift cancelled");
      load({ quiet: true });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /* ----- derived ----- */
  const filtered = useMemo(() => {
    let list = shifts;
    const q = filters.q.trim().toLowerCase();
    if (q) {
      list = list.filter((s) =>
        [s.title, s.facility_name, s.city, s.state, s.specialty]
          .filter(Boolean).join(" ").toLowerCase().includes(q)
      );
    }
    if (filters.shiftType) list = list.filter((s) => (s.shift_type || "").toLowerCase() === filters.shiftType.toLowerCase());
    if (filters.minRate)   list = list.filter((s) => Number(s.hourly_rate || 0) >= Number(filters.minRate));
    list = [...list].sort((a, b) => {
      if (sort === "rate_desc") return (b.hourly_rate||0) - (a.hourly_rate||0);
      if (sort === "rate_asc")  return (a.hourly_rate||0) - (b.hourly_rate||0);
      const da = a.start_time ? new Date(a.start_time).getTime() : 0;
      const db = b.start_time ? new Date(b.start_time).getTime() : 0;
      return sort === "date_asc" ? da - db : db - da;
    });
    return list;
  }, [shifts, filters.q, filters.shiftType, filters.minRate, sort]);

  const saved = useMemo(() => filtered.filter((s) => bookmarks.ids.has(s.id)), [filtered, bookmarks.ids]);

  const byDay = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      const d = (s.start_time || "").slice(0, 10) || "unscheduled";
      (map[d] ||= []).push(s);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // Insights
  const insights = useMemo(() => {
    const rates = filtered.map((s) => Number(s.hourly_rate || 0)).filter(Boolean);
    const max = rates.length ? Math.max(...rates) : 0;
    const avg = rates.length ? Math.round(rates.reduce((a, b) => a+b, 0) / rates.length) : 0;
    const specialties = new Set(filtered.map((s) => s.specialty).filter(Boolean));
    return { total: filtered.length, maxRate: max, avgRate: avg, specialtyCount: specialties.size };
  }, [filtered]);

  const hasActiveFilters = !!(filters.specialty || filters.state || filters.shiftType || filters.q || filters.minRate);
  const reset = () => setFilters({ specialty: "", state: "", shiftType: "", q: "", minRate: "" });

  /* ---------------- Render ---------------- */
  return (
    <div className="space-y-6" data-testid="shift-marketplace">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-trital-red">Marketplace</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">PRN shifts</h1>
          <p className="text-slate-600 text-[14px]">Browse verified shifts. Filter, accept, and get paid.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load({ quiet: true })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 text-[12px] font-semibold hover:bg-slate-50"
            data-testid="shift-refresh"
            title="Refresh shifts"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} strokeWidth={1.8} /> Refresh
          </button>
          <div className="inline-flex rounded-full bg-slate-100 p-1">
            {[
              { k: "list",     Icon: List,         l: "List" },
              { k: "calendar", Icon: CalendarDays, l: "Calendar" },
              { k: "map",      Icon: MapIcon,      l: "Map" },
              { k: "saved",    Icon: Bookmark,     l: `Saved (${bookmarks.ids.size})` },
            ].map(({ k, Icon, l }) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`px-3 py-1.5 rounded-full text-[11.5px] font-semibold inline-flex items-center gap-1 ${view === k ? "bg-white text-trital-navy shadow-sm" : "text-slate-600"}`}
                data-testid={`shift-view-${k}`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="shift-insights">
        <InsightCard Icon={Sparkles}     label="Open shifts"       value={insights.total} />
        <InsightCard Icon={DollarSign}   label="Top rate"          value={insights.maxRate ? `$${insights.maxRate}/hr` : "—"} />
        <InsightCard Icon={TrendingUp}   label="Avg rate"          value={insights.avgRate ? `$${insights.avgRate}/hr` : "—"} />
        <InsightCard Icon={Stethoscope}  label="Specialties"       value={insights.specialtyCount} />
      </div>

      {/* Filters bar */}
      <div className="rounded-2xl bg-white border border-slate-200 p-3 shadow-card space-y-3" data-testid="shift-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" strokeWidth={1.8} />
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.8} />
            <input
              type="text"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search title, facility, city…"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy"
              data-testid="filter-q"
            />
          </div>
          <select value={filters.specialty} onChange={(e) => setFilters({ ...filters, specialty: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-[13px]" data-testid="filter-specialty">
            <option value="">All specialties</option>
            {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-[13px]" data-testid="filter-state">
            <option value="">All states</option>
            {STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.shiftType} onChange={(e) => setFilters({ ...filters, shiftType: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-[13px]" data-testid="filter-shift-type">
            <option value="">Any time</option>
            {SHIFT_TYPES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="relative">
            <DollarSign className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.8} />
            <input
              type="number"
              min={0}
              placeholder="Min rate"
              value={filters.minRate}
              onChange={(e) => setFilters({ ...filters, minRate: e.target.value })}
              className="pl-8 pr-3 py-2 w-28 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy"
              data-testid="filter-min-rate"
            />
          </div>
          <div className="ml-auto inline-flex items-center gap-2">
            <SortDesc className="w-4 h-4 text-slate-500" strokeWidth={1.8} />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-[12.5px]" data-testid="filter-sort">
              {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Active</span>
            {filters.q && <Chip label={`Search: ${filters.q}`} onX={() => setFilters({ ...filters, q: "" })} />}
            {filters.specialty && <Chip label={filters.specialty} onX={() => setFilters({ ...filters, specialty: "" })} />}
            {filters.state && <Chip label={filters.state} onX={() => setFilters({ ...filters, state: "" })} />}
            {filters.shiftType && <Chip label={filters.shiftType} onX={() => setFilters({ ...filters, shiftType: "" })} />}
            {filters.minRate && <Chip label={`≥ $${filters.minRate}/hr`} onX={() => setFilters({ ...filters, minRate: "" })} />}
            <button onClick={reset} className="ml-auto text-[12px] text-slate-500 hover:text-trital-red" data-testid="filter-reset">Reset all</button>
          </div>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div className="grid gap-3" data-testid="shift-loading">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[120px] rounded-2xl bg-white border border-slate-200 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {view === "list" && (
            <ShiftList
              shifts={filtered}
              user={user}
              bookmarks={bookmarks}
              act={act}
              emptyLabel={hasActiveFilters ? "No shifts match your filters." : "No open shifts right now — check back soon."}
              onReset={hasActiveFilters ? reset : null}
            />
          )}
          {view === "saved" && (
            <ShiftList
              shifts={saved}
              user={user}
              bookmarks={bookmarks}
              act={act}
              emptyLabel="You haven't bookmarked any shifts yet."
              onReset={null}
            />
          )}
          {view === "calendar" && <CalendarView days={byDay} />}
          {view === "map" && <MapView shifts={filtered} />}
        </>
      )}
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */
function InsightCard({ Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-card">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      </div>
      <div className="mt-2 font-display font-black text-[22px] text-slate-900 tracking-tight">{value}</div>
    </div>
  );
}

function Chip({ label, onX }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[11px] font-semibold">
      {label}
      <button onClick={onX} className="hover:text-trital-red" aria-label={`Remove ${label}`}><X className="w-3 h-3" strokeWidth={2} /></button>
    </span>
  );
}

function ShiftList({ shifts, user, bookmarks, act, emptyLabel, onReset }) {
  if (!shifts.length) {
    return (
      <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center" data-testid="shift-empty">
        <div className="w-12 h-12 mx-auto rounded-xl bg-slate-100 flex items-center justify-center">
          <Stethoscope className="w-5 h-5 text-slate-500" strokeWidth={1.8} />
        </div>
        <div className="mt-4 font-display font-bold text-[16px] text-slate-900">{emptyLabel}</div>
        <p className="mt-1 text-[13px] text-slate-500">New shifts post throughout the day — hit Refresh or widen your filters.</p>
        {onReset && (
          <button onClick={onReset} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold hover:bg-trital-navy-deep" data-testid="shift-empty-reset">
            Reset filters
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="grid gap-3" data-testid="shift-list">
      {shifts.map((s, i) => <ShiftCard key={s.id} s={s} i={i} user={user} bookmarks={bookmarks} act={act} />)}
    </div>
  );
}

function ShiftCard({ s, i, user, bookmarks, act }) {
  const isBooked = bookmarks.ids.has(s.id);
  const isMine = s.accepted_by === user?.id;
  const when = s.start_time ? new Date(s.start_time) : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i * 0.03, 0.4) }}
      className="relative rounded-2xl bg-white border border-slate-200 p-5 shadow-card hover:shadow-card-hover transition-all"
      data-testid={`shift-card-${s.id}`}
    >
      <button
        onClick={() => bookmarks.toggle(s.id)}
        className={`absolute top-3 right-3 p-1.5 rounded-md transition-colors ${isBooked ? "text-trital-red bg-red-50" : "text-slate-400 hover:text-trital-red hover:bg-slate-50"}`}
        aria-label={isBooked ? "Remove bookmark" : "Bookmark shift"}
        data-testid={`shift-bookmark-${s.id}`}
      >
        {isBooked ? <BookmarkCheck className="w-4 h-4" strokeWidth={1.8} /> : <Bookmark className="w-4 h-4" strokeWidth={1.8} />}
      </button>
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between pr-8">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-trital-navy text-white flex items-center justify-center shrink-0">
            <Stethoscope className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-slate-900 leading-tight">{s.title}</div>
            <div className="mt-1 text-[12px] text-slate-500 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" strokeWidth={1.8} /> {s.facility_name}</span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.8} /> {s.city}, {s.state}</span>
              {when && (<>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.8} /> {relativeDate(when)} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </>)}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{s.specialty}</span>
              {s.shift_type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{s.shift_type}</span>}
              {s.certification_required && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold uppercase tracking-wider inline-flex items-center gap-0.5"><BadgeCheck className="w-2.5 h-2.5" strokeWidth={2}/>{s.certification_required}</span>}
              {isMine && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-bold uppercase tracking-wider">My shift</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="font-display font-black text-trital-navy text-[26px] tracking-tight leading-none">${s.hourly_rate}<span className="text-[14px] font-bold text-slate-400">/hr</span></div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Clinician pay</div>
          </div>
          {user?.role === "clinician" && s.status === "open" && !isMine && (
            <div className="flex gap-2">
              <button onClick={() => act(s.id, "accept")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep" data-testid={`shift-accept-${s.id}`}>
                Accept <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
              <button onClick={() => act(s.id, "decline")} className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-50" data-testid={`shift-decline-${s.id}`}>Decline</button>
            </div>
          )}
          {isMine && (
            <button onClick={() => act(s.id, "cancel")} className="px-4 py-2 rounded-full bg-trital-red text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-red-deep" data-testid={`shift-cancel-${s.id}`}>Cancel</button>
          )}
          {s.status !== "open" && !isMine && (
            <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider font-bold">{s.status}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function CalendarView({ days }) {
  if (!days.length) return <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-card text-slate-500 text-center">No shifts scheduled.</div>;
  return (
    <div className="space-y-4" data-testid="shift-calendar">
      {days.map(([day, items]) => (
        <div key={day} className="rounded-2xl bg-white border border-slate-200 p-4 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-trital-red">{day === "unscheduled" ? "Unscheduled" : new Date(day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{items.length} shift{items.length !== 1 ? "s" : ""}</div>
            </div>
          </div>
          <div className="mt-3 divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-[13.5px] truncate">{s.title}</div>
                  <div className="text-[11.5px] text-slate-500 truncate">{s.facility_name} · {s.start_time ? new Date(s.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} · {s.specialty}</div>
                </div>
                <div className="font-display font-bold text-trital-navy shrink-0">${s.hourly_rate}<span className="text-[11px] text-slate-400">/hr</span></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MapView({ shifts }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-card" data-testid="shift-map">
      <div className="flex items-center gap-2 text-slate-700 mb-3">
        <MapIcon className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
        <span className="font-display font-semibold text-slate-900">Shift locations</span>
        <span className="ml-auto text-[11px] text-slate-500">{shifts.length} pins</span>
      </div>
      <div className="relative h-[460px] rounded-xl bg-[linear-gradient(180deg,#f8fafc_0%,#eef2fa_100%)] overflow-hidden border border-slate-200">
        <div className="absolute inset-0 dot-grid opacity-70" />
        {shifts.map((s) => {
          const lng = s.lng ?? -95, lat = s.lat ?? 37;
          const x = ((lng + 125) / 59) * 100;
          const y = (1 - (lat - 25) / 24) * 100;
          return (
            <div
              key={s.id}
              style={{ left: `${Math.max(2, Math.min(98, x))}%`, top: `${Math.max(2, Math.min(95, y))}%` }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-default"
            >
              <div className="w-3 h-3 rounded-full bg-trital-red shadow-md ring-2 ring-white" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 hidden group-hover:block z-10 bg-white border border-slate-200 rounded-xl p-3 shadow-card-hover">
                <div className="font-display font-bold text-[13px]">{s.title}</div>
                <div className="text-[11px] text-slate-500">{s.facility_name} · {s.city}, {s.state}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-display font-bold text-trital-navy">${s.hourly_rate}/hr</span>
                  <span className="text-[9.5px] px-1.5 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{s.specialty}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-slate-500">US continental projection · hover any pin for shift details.</div>
    </div>
  );
}
